import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import StatusBadge from "@/components/shared/StatusBadge";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import api, { extractApiError } from "@/lib/api";
import {
  Plus,
  Building2,
  Hospital,
  Flame,
  Ambulance,
  Search,
  Filter,
  MapPin,
  RefreshCw,
  Phone,
  Hash,
  ChevronDown,
  X,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  POLICE: { icon: Building2, color: "bg-blue-50 text-blue-600", label: "Police" },
  AMBULANCE: { icon: Ambulance, color: "bg-orange-50 text-orange-600", label: "Ambulance" },
  FIRE_DEPARTMENT: { icon: Flame, color: "bg-rose-50 text-rose-600", label: "Fire Department" },
  CUSTOMER: { icon: Building2, color: "bg-emerald-50 text-emerald-600", label: "Customer" },
};

const SERVICE_TYPE_TABS = ["all", "POLICE", "AMBULANCE", "FIRE_DEPARTMENT"];

function formatOrgId(index) {
  return `ORG-${String(index).padStart(3, "0")}`;
}

export default function ServicesManagement() {
  const { can } = useAuth();
  const canUpdateOrganizations = can("ORGANIZATIONS.UPDATE");
  const canActivateOrganizations = can("ORGANIZATIONS.ACTIVATE");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    org_type: "POLICE",
    state: "",
    city: "",
    district: "",
    pincode: "",
    address: "",
    contact: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const navigate = useNavigate();

  // View organization dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  const [viewOrganization, setViewOrganization] = useState(null);

  // Edit organization dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const [editOrgId, setEditOrgId] = useState(null);
  const [editOriginal, setEditOriginal] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    state: "",
    city: "",
    district: "",
    pincode: "",
    address: "",
    contact: "",
    is_active: true,
  });

  // Delete organization (soft-delete via DELETE endpoint)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Filters State
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [organizations, setOrganizations] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const resetFilters = () => {
    setQ("");
    setTypeFilter("all");
    setStatusFilter("all");
    setPage(1);
  };

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page,
        page_size: pageSize,
      };

      if (q.trim()) params.search = q.trim();
      if (typeFilter !== "all") params.org_type = typeFilter;
      if (statusFilter !== "all") params.is_active = statusFilter === "true";

      const { data } = await api.get("/organizations", { params });
      setOrganizations(Array.isArray(data?.data) ? data.data : []);
      setMeta(
        data?.meta || {
          total: 0,
          page,
          page_size: pageSize,
          pages: 1,
        },
      );
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message);
      setOrganizations([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [q, typeFilter, statusFilter, page, pageSize]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const filteredOrganizations = useMemo(() => organizations, [organizations]);

  const activeFiltersCount = [typeFilter !== "all", statusFilter !== "all", !!q.trim()].filter(Boolean).length;

  const resetForm = () => {
    setForm({
      name: "",
      org_type: "POLICE",
      state: "",
      city: "",
      district: "",
      pincode: "",
      address: "",
      contact: "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!form.name.trim()) {
      setSubmitError("Organization name is required.");
      return;
    }

    if (!form.org_type) {
      setSubmitError("Organization type is required.");
      return;
    }

    if (form.org_type === "CUSTOMER") {
      setSubmitError("Customer organizations cannot be created via this form. Customers are created exclusively through the vehicle onboarding flow.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      org_type: form.org_type,
      ...(form.state.trim() ? { state: form.state.trim() } : {}),
      ...(form.city.trim() ? { city: form.city.trim() } : {}),
      ...(form.district.trim() ? { district: form.district.trim() } : {}),
      ...(form.pincode.trim() ? { pincode: form.pincode.trim() } : {}),
      ...(form.address.trim() ? { address: form.address.trim() } : {}),
      ...(form.contact.trim() ? { contact: form.contact.trim() } : {}),
    };

    try {
      setSubmitting(true);
      await api.post("/organizations", payload);
      toast.success(`Onboarded ${form.name.trim()} (${form.org_type})`);
      setOpen(false);
      resetForm();
      await fetchOrganizations();
    } catch (err) {
      const { message } = extractApiError(err);
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewOrganization = async (orgId) => {
    setViewOpen(true);
    setViewOrganization(null);
    setViewError("");
    setViewLoading(true);
    try {
      const { data } = await api.get(`/organizations/${orgId}`);
      setViewOrganization(data || null);
    } catch (err) {
      const { message } = extractApiError(err);
      setViewError(message || "Failed to load organization details.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleEditOrganization = async (orgId) => {
    setEditOpen(true);
    setEditOrgId(orgId);
    setEditLoading(true);
    setEditError("");
    try {
      const { data } = await api.get(`/organizations/${orgId}`);
      setEditOriginal(data || null);
      setEditForm({
        name: data?.name || "",
        state: data?.state || "",
        city: data?.city || "",
        district: data?.district || "",
        pincode: data?.pincode || "",
        address: data?.address || "",
        contact: data?.contact || "",
        is_active: data?.is_active ?? true,
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setEditError(message || "Failed to load organization for editing.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditFormChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpdateOrganization = async () => {
    if (!canUpdateOrganizations) {
      toast.error("You do not have permission to update organizations.");
      return;
    }

    if (!editOrgId || !editOriginal) return;

    const normalizeText = (v) => (v || "").trim();
    const payload = {};

    if (normalizeText(editForm.name) !== normalizeText(editOriginal.name)) {
      payload.name = normalizeText(editForm.name) || undefined;
    }
    if (normalizeText(editForm.state) !== normalizeText(editOriginal.state)) {
      payload.state = normalizeText(editForm.state) || undefined;
    }
    if (normalizeText(editForm.city) !== normalizeText(editOriginal.city)) {
      payload.city = normalizeText(editForm.city) || undefined;
    }
    if (normalizeText(editForm.district) !== normalizeText(editOriginal.district)) {
      payload.district = normalizeText(editForm.district) || undefined;
    }
    if (normalizeText(editForm.pincode) !== normalizeText(editOriginal.pincode)) {
      payload.pincode = normalizeText(editForm.pincode) || undefined;
    }
    if (normalizeText(editForm.address) !== normalizeText(editOriginal.address)) {
      payload.address = normalizeText(editForm.address) || undefined;
    }
    if (normalizeText(editForm.contact) !== normalizeText(editOriginal.contact)) {
      payload.contact = normalizeText(editForm.contact) || undefined;
    }

    if ((editForm.is_active ?? true) !== (editOriginal.is_active ?? true)) {
      payload.is_active = !!editForm.is_active;
    }

    if (!canActivateOrganizations && Object.prototype.hasOwnProperty.call(payload, "is_active")) {
      delete payload.is_active;
    }

    if (!Object.keys(payload).length) {
      toast.message("No changes to update.");
      return;
    }

    setEditSubmitting(true);
    setEditError("");
    try {
      await api.patch(`/organizations/${editOrgId}`, payload);
      toast.success("Organization updated successfully.");
      setEditOpen(false);
      await fetchOrganizations();
    } catch (err) {
      const { message } = extractApiError(err);
      setEditError(message || "Failed to update organization.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!deleteTarget?.id) return;

    setDeleteSubmitting(true);
    try {
      await api.delete(`/organizations/${deleteTarget.id}`);
      toast.success("Organization deleted successfully.");
      setDeleteTarget(null);
      await fetchOrganizations();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to delete organization.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleView = (organization) => {
  navigate(`/admin/service-provider-details/${organization.id}`);
};

  return (
    <div className="space-y-6" data-testid="services-management">
      <PageHeader
        title="Emergency Services"
        subtitle="Manage and coordinate all emergency response organizations."
        actions={
          <Button
            className="bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => {
              setSubmitError("");
              setOpen(true);
            }}
          >
            <Plus size={14} className="mr-1.5" /> Onboard service
          </Button>
        }
      />

      {/* Type Tabs */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <Tabs value={typeFilter} onValueChange={(value) => { setTypeFilter(value); setPage(1); }}>
          <TabsList className="h-auto flex-wrap justify-start gap-2 rounded-2xl bg-slate-100 p-2">
            {SERVICE_TYPE_TABS.map((type) => (
              <TabsTrigger
                key={type}
                value={type}
                className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm"
              >
                {type === "all" ? "All" : TYPE_META[type]?.label || type}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="mt-4 flex flex-col md:flex-row items-center gap-3">
          <div className="relative flex-1 w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, slug, contact or address..."
              className="pl-10 h-11 border-slate-200 bg-slate-50/50"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-11 border-slate-200 gap-2 px-4 text-slate-600">
                  <Filter size={16} /> Filters
                  {activeFiltersCount > 0 && (
                    <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold inline-flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                  <ChevronDown size={14} className="text-slate-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-5" align="end">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Organization Status</Label>
                    <Select
                      value={statusFilter}
                      onValueChange={(value) => {
                        setStatusFilter(value);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Active Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                        <SelectItem value="all">All Status</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" className="w-full text-slate-500 h-10 gap-2 hover:bg-slate-50" onClick={resetFilters}>
                    <X size={14} /> Reset Filters
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-11 border-slate-200 bg-white w-[110px]">
                <SelectValue placeholder="Size" />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" className="h-11 border-slate-200 gap-2 px-4 text-slate-600" onClick={fetchOrganizations}>
              <RefreshCw size={16} /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-slate-500">
          {loading ? "Loading organizations..." : `${meta.total} organizations found`}
        </span>
        <span className="text-xs text-slate-500">
          Page {meta.page || page} of {meta.pages || 1}
        </span>
      </div>

      {/* Services Grid */}
      <div className="card_fix_hei_em">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {filteredOrganizations.map((organization, index) => {
          const orgType = organization.org_type || "";
          const Meta = TYPE_META[orgType] || { icon: Building2, color: "bg-slate-50 text-slate-600", label: orgType || "Organization" };
          const orgNumber = formatOrgId((page - 1) * pageSize + index + 1);
          return (
            <div
              key={organization.id}
              className="group cursor-pointer rounded-3xl border border-slate-100 bg-white p-6 shadow-sm transition-all hover:shadow-xl hover:border-slate-200 relative overflow-hidden"
              onClick={() => handleView(organization)}
            >
              {/* Subtle Icon Background */}
              <div className={`absolute -right-4 -top-4 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity duration-500 ${Meta.color}`}>
                <Meta.icon size={120} />
              </div>

              <div className="relative z-10 space-y-6">
                <div className="flex items-start justify-between">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-2xl shadow-sm ${Meta.color}`}>
                    <Meta.icon size={22} strokeWidth={2} />
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={organization.is_active ? "active" : "inactive"} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleView(organization); }}>
                          <Eye size={14} /> View
                        </DropdownMenuItem>
                        {canUpdateOrganizations ? (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditOrganization(organization.id); }}>
                            <Pencil size={14} /> Edit
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem
                          className="text-rose-600 focus:text-rose-700"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: organization.id, name: organization.name || "this organization" }); }}
                        >
                          <Trash2 size={14} /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Hash size={12} className="text-slate-400" />
                    <span className="font-mono text-[10px] font-black uppercase tracking-widest text-slate-400">{orgNumber}</span>
                  </div>
                  <h3 className="text-xl font-black text-slate-900 group-hover:text-blue-600 transition-colors">{organization.name}</h3>
                  <div className="flex items-center gap-1.5 mt-1 text-slate-500">
                    <MapPin size={14} className="shrink-0" />
                    <p className="text-[13px] font-bold">
                      {[organization.city, organization.state].filter(Boolean).join(", ") || organization.address || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="rounded-2xl bg-slate-50/50 p-4 border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone size={12} className="text-slate-400" />
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Contact</p>
                    </div>
                    <p className="text-[13px] font-mono font-bold text-slate-900">{organization.contact || "N/A"}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50/50 p-4 border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 size={12} className="text-slate-400" />
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Type</p>
                    </div>
                    <p className="text-[13px] font-bold text-slate-900">{Meta.label}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="rounded-2xl bg-slate-50/50 p-4 border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={12} className="text-slate-400" />
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Slug</p>
                    </div>
                    <p className="text-[13px] font-bold text-slate-900 break-all">{organization.slug || "N/A"}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50/50 p-4 border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw size={12} className="text-slate-400" />
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Updated</p>
                    </div>
                    <p className="text-[13px] font-bold text-slate-900">{organization.updated_at ? new Date(organization.updated_at).toLocaleDateString() : "N/A"}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {!loading && filteredOrganizations.length === 0 && (
          <div className="col-span-full rounded-3xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center text-slate-500">
            <div className="flex flex-col items-center gap-3">
              <Filter size={40} className="text-slate-100" />
              <p className="font-medium">No organizations found matching your criteria.</p>
              <button onClick={resetFilters} className="text-sm text-blue-600 font-bold hover:underline">Clear all filters</button>
            </div>
          </div>
        )}
      </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-4 py-4 rounded-2xl shadow-sm">
        <Button
          variant="outline"
          className="border-slate-200"
          disabled={loading || page <= 1}
          onClick={() => setPage((previousPage) => Math.max(1, previousPage - 1))}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          className="border-slate-200"
          disabled={loading || page >= (meta.pages || 1)}
          onClick={() => setPage((previousPage) => Math.min(meta.pages || 1, previousPage + 1))}
        >
          Next
        </Button>
      </div>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setSubmitError("");
            resetForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-md rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Onboard Service</DialogTitle>
            <DialogDescription className="font-bold text-slate-500">Add a new organization to the platform.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest ml-1">Organization Type</Label>
              <Select value={form.org_type} onValueChange={(v) => setForm({ ...form, org_type: v })}>
                <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="POLICE">Police</SelectItem>
                  <SelectItem value="AMBULANCE">Ambulance</SelectItem>
                  <SelectItem value="FIRE_DEPARTMENT">Fire Department</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest ml-1">Organization Name</Label>
              <Input
                className="h-12 rounded-xl border-slate-200 bg-white"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. City Emergency Control"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest ml-1">Contact (Optional)</Label>
              <Input
                className="h-12 rounded-xl bg-white border-slate-200"
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                placeholder="e.g. +91 9876543210"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest ml-1">State (Optional)</Label>
                {/* <Input
                  className="h-12 rounded-2xl border-slate-200"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  placeholder="e.g. Maharashtra"
                /> */}
                <Select
                  
                    
                  >
                    <SelectTrigger className="bg-white border-slate-200 h-11">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                
                    <SelectContent>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                    </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest ml-1">City (Optional)</Label>
                {/* <Input
                  className="h-12 rounded-2xl border-slate-200"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="e.g. Mumbai"
                /> */}
                <Select
                    
                  >
                    <SelectTrigger className="bg-white border-slate-200 h-11">
                      <SelectValue placeholder="Select City" />
                    </SelectTrigger>
                
                    <SelectContent>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                    </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest ml-1">District (Optional)</Label>
                {/* <Input
                  className="h-12 rounded-2xl border-slate-200"
                  value={form.district}
                  onChange={(e) => setForm({ ...form, district: e.target.value })}
                  placeholder="e.g. Mumbai Suburban"
                /> */}
                             <Select
                   
                  >
                    <SelectTrigger className="bg-white border-slate-200 h-11">
                      <SelectValue placeholder="Select District" />
                    </SelectTrigger>
                
                    <SelectContent>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                    </SelectContent>
                              </Select> 
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest ml-1">Pincode (Optional)</Label>
                <Input
                  className="h-12 rounded-xl bg-white border-slate-200"
                  value={form.pincode}
                  onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                  placeholder="e.g. 400001"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest ml-1">Address (Optional)</Label>
              <Input
                className="h-12 rounded-xl bg-white border-slate-200"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="e.g. Main road, area, landmark"
              />
            </div>

            {submitError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {submitError}
              </div>
            )}

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-2xl h-12 font-bold" disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-2xl h-12 px-8 font-black" disabled={submitting}>
                {submitting ? "Onboarding..." : "Confirm Onboarding"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Organization */}
      <Dialog open={viewOpen} onOpenChange={(nextOpen) => { if (!nextOpen) { setViewOpen(false); setViewOrganization(null); setViewError(""); } }}>
        <DialogContent className="sm:max-w-2xl rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">
              {viewLoading ? "Loading..." : viewOrganization?.name || "Organization Details"}
            </DialogTitle>
            <DialogDescription className="font-bold text-slate-500">
              {viewOrganization?.org_type ? (TYPE_META[viewOrganization.org_type]?.label || viewOrganization.org_type) : "Viewing organization details"}
            </DialogDescription>
          </DialogHeader>

          {viewLoading && (
            <div className="flex items-center justify-center py-14 text-slate-500">
              <Loader2 size={22} className="animate-spin mr-2" /> Loading organization...
            </div>
          )}

          {!viewLoading && viewError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {viewError}
            </div>
          )}

          {!viewLoading && viewOrganization && (() => {
            const Field = ({ label, value, mono = false }) => (
              <div className="space-y-1.5">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{label}</p>
                <div className={`flex min-h-12 items-center rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-2 text-sm ${mono ? "font-mono" : ""} ${value ? "text-slate-900" : "text-slate-400 italic"}`}>
                  {value || "—"}
                </div>
              </div>
            );

            return (
              <div className="space-y-5 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${viewOrganization.is_active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"}`}>
                    {viewOrganization.is_active ? "● Active" : "○ Inactive"}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">
                    {TYPE_META[viewOrganization.org_type]?.label || viewOrganization.org_type || "Organization"}
                  </span>
                </div>

                <div>
                  <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">Primary Information</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Organization Name" value={viewOrganization.name} />
                    <Field label="Contact" value={viewOrganization.contact} mono />
                    <div className="col-span-2">
                      <Field label="Address" value={viewOrganization.address} />
                    </div>
                  </div>
                </div>

                <div>
                  <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">Location</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="State" value={viewOrganization.state} />
                    <Field label="District" value={viewOrganization.district} />
                    <Field label="City" value={viewOrganization.city} />
                    <Field label="Pincode" value={viewOrganization.pincode} mono />
                  </div>
                </div>



                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit Organization */}
      <Dialog open={editOpen} onOpenChange={(nextOpen) => { if (!nextOpen && !editSubmitting) setEditOpen(false); }}>
        <DialogContent className="sm:max-w-2xl rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Edit Organization</DialogTitle>
            <DialogDescription className="font-bold text-slate-500">
              Partial update via /organizations/{'{'}org_id{'}'}
            </DialogDescription>
          </DialogHeader>

          {editLoading ? (
            <div className="flex items-center justify-center py-14 text-slate-500">
              <Loader2 size={22} className="animate-spin mr-2" /> Loading organization...
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Organization Name</Label>
                <Input value={editForm.name} onChange={(e) => handleEditFormChange("name", e.target.value)} />
              </div>

              <div className="space-y-1.5">
                <Label>Contact</Label>
                <Input value={editForm.contact} onChange={(e) => handleEditFormChange("contact", e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>State</Label>
                  <Input value={editForm.state} onChange={(e) => handleEditFormChange("state", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>City</Label>
                  <Input value={editForm.city} onChange={(e) => handleEditFormChange("city", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>District</Label>
                  <Input value={editForm.district} onChange={(e) => handleEditFormChange("district", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Pincode</Label>
                  <Input value={editForm.pincode} onChange={(e) => handleEditFormChange("pincode", e.target.value)} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Address</Label>
                <Input value={editForm.address} onChange={(e) => handleEditFormChange("address", e.target.value)} />
              </div>

              <div className="space-y-1.5">
                <Label>Active Status</Label>
                <Select
                  value={editForm.is_active ? "true" : "false"}
                  onValueChange={(v) => handleEditFormChange("is_active", v === "true")}
                  disabled={!canActivateOrganizations}
                >
                  <SelectTrigger
                    className={!canActivateOrganizations ? "bg-slate-100 cursor-not-allowed" : undefined}
                    aria-readonly={!canActivateOrganizations}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {editError}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSubmitting}>Cancel</Button>
                <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={handleUpdateOrganization} disabled={editSubmitting}>
                  {editSubmitting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving...</> : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Organization */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !deleteSubmitting) setDeleteTarget(null);
        }}
        title="Delete organization?"
        description={
          <>
            You are about to delete <span className="font-bold text-slate-900">{deleteTarget?.name}</span>.
            {" "}This is a soft delete and will mark the organization as inactive.
          </>
        }
        confirmLabel="Yes, delete"
        variant="danger"
        loading={deleteSubmitting}
        onConfirm={handleDeleteOrganization}
      />
    </div>
  );
}
